/**
 * Small geometry and colour helpers of the notes module (W1-06): Phira's letterboxed viewport (R§1.2), the
 * off-screen quad cull with its +-1 quirk (R§4.5 item 6), the true visible area (ghost filtering) and the explicit
 * colour clamp (u8 vertex colours, R§4.4).
 */

import type { PackageSettings } from '../../model/types';
import type { RGBAf } from '../../util/types';
import type { PhiraPlayerSettings, Viewport } from '../types';
import { OFFSCREEN_BOUND } from './constants';

/**
 * Phira `update_size` letterboxing (R§1.2): chart aspect = player override ?? info aspect; without
 * forceAspectRatio a narrower window uses the window aspect; the ar-shaped rect is fitted and centred, offsets
 * rounded, sizes truncated. (W1-05a owns the engine's viewport; the notes module needs it for read-time checks
 * that receive only window sizes.)
 */
export function phiraViewport(
  winW: number,
  winH: number,
  pkg: Pick<PackageSettings, 'aspectRatio' | 'forceAspectRatio'>,
  player: Pick<PhiraPlayerSettings, 'aspectOverride'>,
): Viewport {
  const chartAr = player.aspectOverride ?? pkg.aspectRatio;
  const ar = pkg.forceAspectRatio ? chartAr : Math.min(chartAr, winW / winH);
  let w = winH * ar;
  let h = winH;
  if (w > winW) {
    w = winW;
    h = winW / ar;
  }
  const vx = Math.round((winW - w) / 2);
  const vy = Math.round((winH - h) / 2);
  return { winW, winH, vx, vy, W: Math.trunc(w), H: Math.trunc(h), ar };
}

/**
 * Phira's off-screen test on the 4 world corners of a quad (8 floats at `off`): culled when all x > 1, all
 * x < -1, all y > 1 or all y < -1. y uses +-1, not +-1/ar (quirk: on portrait aspects on-screen quads with
 * |y| > 1 are culled too).
 */
export function quadOffscreen(p: ArrayLike<number>, off: number): boolean {
  const x0 = p[off] ?? 0;
  const y0 = p[off + 1] ?? 0;
  const x1 = p[off + 2] ?? 0;
  const y1 = p[off + 3] ?? 0;
  const x2 = p[off + 4] ?? 0;
  const y2 = p[off + 5] ?? 0;
  const x3 = p[off + 6] ?? 0;
  const y3 = p[off + 7] ?? 0;
  const b = OFFSCREEN_BOUND;
  return (
    (x0 > b && x1 > b && x2 > b && x3 > b) ||
    (x0 < -b && x1 < -b && x2 < -b && x3 < -b) ||
    (y0 > b && y1 > b && y2 > b && y3 > b) ||
    (y0 < -b && y1 < -b && y2 < -b && y3 < -b)
  );
}

/**
 * Whether the axis-aligned bounds of a world quad overlap the visible chart area [-1, 1] x [-1/ar, 1/ar]
 * (ghost outlines are listed only for notes that would be on screen).
 */
export function quadInView(p: ArrayLike<number>, off: number, ar: number): boolean {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let k = 0; k < 4; k++) {
    const x = p[off + 2 * k] ?? Number.NaN;
    const y = p[off + 2 * k + 1] ?? Number.NaN;
    if (Number.isNaN(x) || Number.isNaN(y)) continue;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  const yb = 1 / ar;
  return maxX >= -1 && minX <= 1 && maxY >= -yb && minY <= yb;
}

/** Float colour channel (0..1, any value) to a byte: explicit clamp then round (Uint8Array would wrap). */
export function colorByte(v: number): number {
  if (!(v > 0)) return 0;
  if (v >= 1) return 255;
  return Math.round(v * 255);
}

/** RGBAf with every channel clamped to 0..1 (vertex colours saturate in Phira). */
export function clampRgbaf(r: number, g: number, b: number, a: number): RGBAf {
  return [clamp01(r), clamp01(g), clamp01(b), clamp01(a)];
}

export function clamp01(v: number): number {
  if (!(v > 0)) return 0;
  return v >= 1 ? 1 : v;
}
